import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { LuFileText, LuShield, LuStar } from "react-icons/lu";
import { ACTIONS, URLS } from "../../_config";
import { useConsentApi } from "../../store/api/hooks/consentApiHook";
import { useUserSelector } from "../../store/selectors/userSelector";
import Button from "../elements/button";
import DocumentViewer from "./DocumentViewer";
import SmsConsentFields from "./SmsConsentFields";
import { decodeSessionPhone, isValidTenDigitPhone, toSafeHttpsUrl, toTenDigitPhone } from "./consentUtils";
import { formatPhoneNumber } from "../PhoneInput/CustomPhoneInput";
import { useConsentSignOut } from "./useConsentSignOut";

interface ConsentContent {
  termsAndConditionsUrl?: string | null;
  privacyPolicyUrl?: string | null;
}

type DocCode = "TERMS" | "PRIVACY";

/**
 * Blocking first-sign-in consent window for the Owner. Non-dismissible: the only
 * exits are "Accept & Continue" and "Sign out" (which records nothing).
 */
const OwnerConsentModal: React.FC = () => {
  const { t } = useTranslation("consent");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user: any = useUserSelector();
  const consentApi = useConsentApi();
  const { signOut, isSigningOut } = useConsentSignOut();

  const [content, setContent] = useState<ConsentContent | null>(null);
  const [contentLoading, setContentLoading] = useState(true);
  const [contentError, setContentError] = useState(false);
  const [viewing, setViewing] = useState<DocCode | null>(null);

  const prefilledPhone = formatPhoneNumber(decodeSessionPhone(user?.mobileNumber));
  const [acknowledged, setAcknowledged] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [phone, setPhone] = useState(prefilledPhone);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);

  const loadContent = () => {
    setContentLoading(true);
    setContentError(false);
    consentApi.getConsentContent(
      {},
      (resp: any) => {
        setContent(resp?.data ?? null);
        setContentLoading(false);
      },
      () => {
        setContentError(true);
        setContentLoading(false);
      }
    );
  };

  useEffect(() => {
    loadContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const documents: { code: DocCode; name: string; url: string | null }[] = [
    { code: "TERMS", name: t("documents.terms"), url: toSafeHttpsUrl(content?.termsAndConditionsUrl) },
    { code: "PRIVACY", name: t("documents.privacy"), url: toSafeHttpsUrl(content?.privacyPolicyUrl) },
  ];
  const viewingDoc = documents.find((d) => d.code === viewing);

  // Phone and SMS flag move together: unticking restores the pre-filled number.
  const handleSmsToggle = (checked: boolean) => {
    setSmsOptIn(checked);
    if (!checked) {
      setPhone(prefilledPhone);
      setPhoneTouched(false);
    }
  };

  const phoneValid = !smsOptIn || isValidTenDigitPhone(phone);
  const canAccept = acknowledged && phoneValid && !!content && !contentLoading && !isAccepting && !isSigningOut;

  const handleAccept = () => {
    if (!canAccept) return;
    setIsAccepting(true);
    consentApi.acceptMerchantConsent(
      {
        acknowledged: true,
        smsOptIn,
        mobileNumber: smsOptIn ? toTenDigitPhone(phone) : null,
      },
      () => {
        // Only a confirmed API success opens the gate.
        dispatch({ type: ACTIONS.USER.USER, payload: { ...user, isTermAccepted: true } });
        navigate(URLS.DASHBOARD, { replace: true });
      },
      (message: string) => {
        setIsAccepting(false);
        toast.error(message || t("toasts.acceptFailed"));
      }
    );
  };

  return (
    <>
      <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="consent-title"
          className="flex flex-col w-[60vw] min-w-[320px] max-w-[880px] max-h-[92vh] overflow-hidden bg-white rounded-lg shadow-md"
        >
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-200">
            <div>
              <p id="consent-title" className="text-lg font-semibold text-gray-900">{t("modal.title")}</p>
              <p className="text-sm text-gray-500">{t("modal.subtitle")}</p>
            </div>
            <span className="px-2.5 py-1 text-sm font-medium text-purple-700 bg-purple-100 rounded-full">
              {t("modal.ownerBadge")}
            </span>
          </div>

          <div className="p-5 space-y-4 overflow-y-auto">
            <div className="flex gap-2.5 p-3 text-sm leading-relaxed text-purple-900 border border-purple-200 rounded-lg bg-purple-50">
              <LuStar size={14} className="mt-0.5 shrink-0" />
              <p>{t("modal.banner")}</p>
            </div>

            <section className="p-4 space-y-3 border border-gray-200 rounded-xl">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-gray-900">{t("terms.title")}</h3>
                <span className="px-2 py-0.5 text-xs font-medium text-red-600 bg-red-50 rounded-full">
                  {t("mandatory")}
                </span>
              </div>
              {contentLoading && (
                <>
                  <div className="h-11 rounded-lg bg-gray-100 animate-pulse" />
                  <div className="h-11 rounded-lg bg-gray-100 animate-pulse" />
                </>
              )}
              {contentError && (
                <p className="text-sm text-red-600" role="alert">
                  {t("terms.loadError")}{" "}
                  <button type="button" className="font-medium underline" onClick={loadContent}>
                    {t("retry")}
                  </button>
                </p>
              )}
              {!contentLoading &&
                !contentError &&
                documents.map((doc) => (
                  <button
                    key={doc.code}
                    type="button"
                    onClick={() => setViewing(doc.code)}
                    className="flex items-center w-full gap-2.5 px-3 py-2.5 text-left border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <span className="text-gray-500">
                      {doc.code === "PRIVACY" ? <LuShield size={15} /> : <LuFileText size={15} />}
                    </span>
                    <span className="text-base font-semibold text-blue-600 underline">{doc.name}</span>
                    <span className="px-2 py-0.5 text-xs font-medium text-red-600 bg-red-50 rounded-full">
                      {t("mandatory")}
                    </span>
                  </button>
                ))}
              <label htmlFor="consent-ack" className="flex items-start gap-2.5 cursor-pointer">
                <input
                  id="consent-ack"
                  type="checkbox"
                  className="checkbox checkbox-sm shrink-0"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                />
                {/* Plain text on purpose: the acknowledgement carries no links. */}
                <span className="text-md text-gray-800">{t("terms.acknowledgement")}</span>
              </label>
            </section>

            <section className="p-4 space-y-3 border border-gray-200 rounded-xl">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-gray-900">{t("sms.title")}</h3>
                <span className="px-2 py-0.5 text-xs font-medium text-gray-500 bg-gray-100 rounded-full">
                  {t("optional")}
                </span>
              </div>
              <SmsConsentFields
                idPrefix="consent"
                checked={smsOptIn}
                onCheckedChange={handleSmsToggle}
                phone={phone}
                onPhoneChange={setPhone}
                onPhoneBlur={() => setPhoneTouched(true)}
                phoneError={smsOptIn && phoneTouched && !phoneValid ? t("sms.phoneInvalid") : undefined}
              />
            </section>
          </div>

          <div className="flex flex-col gap-3 px-5 py-4 border-t border-gray-200 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm leading-relaxed text-gray-500 sm:max-w-xl">{t("modal.footer")}</p>
            <div className="flex items-center gap-2 shrink-0">
              <Button theme="secondary" disabled={isSigningOut || isAccepting} onClick={signOut}>
                {isSigningOut ? t("signingOut") : t("signOut")}
              </Button>
              <Button disabled={!canAccept} working={isAccepting} onClick={handleAccept}>
                {t("modal.accept")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <DocumentViewer title={viewingDoc?.name ?? null} url={viewingDoc?.url ?? null} onClose={() => setViewing(null)} />
    </>
  );
};

export default OwnerConsentModal;
